import * as React from 'react';
import { t } from '@reos/i18n';
import { fetchParcelProximity, type ParcelProximityDto } from '../lib/parcel-map-api.js';

/**
 * Krok NAP14 bod 1 (seznam A30) — SEKCE „OKOLI PARCELY" V PANELU MAPY.
 *
 * Vzdalenosti a pocty pocita server (`GET …/cases/:id/map/proximity`) nad
 * zamcenou geometrii parcely; tady se jen vykresluji. Klient nezna zadnou
 * URL zdroje ani vzorec — stejne pravidlo jako u katalogu vrstev (M1/MAP4).
 *
 * Sekce se NACITA AZ PO ROZBALENI: server pri tom cte zive externi registry,
 * takze otevreni mapy je nesmi volat (stejna uvaha jako chip Panorama
 * v `ParcelPanel`, ktery obrazek tahne az po kliknuti).
 *
 * Porucha zdroje se NIKDY nekresli jako nula — kazdy radek bez hodnoty rika
 * svuj duvod (`unavailableReason`).
 */

type ProximityItem = ParcelProximityDto['items'][number];

const LAYER_LABEL_KEY: Readonly<Record<string, Parameters<typeof t>[0]>> = {
  heritage: 'webAdmin.parcelMap.proximity.layer.heritage',
  nature_protected_areas: 'webAdmin.parcelMap.proximity.layer.nature_protected_areas',
  natura2000: 'webAdmin.parcelMap.proximity.layer.natura2000',
  archaeology: 'webAdmin.parcelMap.proximity.layer.archaeology',
  slope_instability: 'webAdmin.parcelMap.proximity.layer.slope_instability',
  pollution_sources: 'webAdmin.parcelMap.proximity.layer.pollution_sources',
};

function layerLabel(layerId: string): string {
  const key = LAYER_LABEL_KEY[layerId];
  /* Vrstva pribyla do OVERLAY-LAYERS.yaml a nazev jeste nema: radek se
     NESKRYVA (to by byla tise chybejici odpoved), ukaze se s id vrstvy. */
  return key === undefined
    ? t('webAdmin.parcelMap.proximity.layerUnknown', { locale: 'cs', values: { id: layerId } })
    : t(key, { locale: 'cs' });
}

function metres(value: number): string {
  return String(Math.round(value));
}

function itemValue(item: ProximityItem): string {
  if (item.unavailableReason === 'source_unavailable') {
    return t('webAdmin.parcelMap.proximity.sourceUnavailable', { locale: 'cs' });
  }
  if (item.unavailableReason === 'none_in_search_radius') {
    return t('webAdmin.parcelMap.proximity.noneInRadius', {
      locale: 'cs',
      values: { radius: metres(item.radiusM) },
    });
  }
  if (item.kind === 'count') {
    return t('webAdmin.parcelMap.proximity.count', {
      locale: 'cs',
      values: { count: String(item.count ?? 0), radius: metres(item.radiusM) },
    });
  }
  if (item.distanceM === null) {
    return t('webAdmin.parcelMap.proximity.sourceUnavailable', { locale: 'cs' });
  }
  /* Nula znamena „plocha zasahuje do parcely", ne „nula metru daleko" —
     vzdalenost se meri k hrane, takze prekryv vyjde presne 0. */
  return item.distanceM === 0
    ? t('webAdmin.parcelMap.proximity.distanceInside', { locale: 'cs' })
    : t('webAdmin.parcelMap.proximity.distance', {
        locale: 'cs',
        values: { m: metres(item.distanceM) },
      });
}

function ProximityRow({ item }: { readonly item: ProximityItem }): React.JSX.Element {
  const testId = item.layerId.replace(/_/gu, '-');
  return (
    <li
      style={{
        borderTop: '1px solid var(--rh-rule)',
        padding: '7px 0',
        display: 'flex',
        gap: 10,
        alignItems: 'baseline',
        flexWrap: 'wrap',
      }}
      data-testid={`map-proximity-row-${testId}`}
    >
      <span style={{ fontSize: 'var(--rh-size-meta)', color: 'var(--rh-text-muted)', minWidth: 150 }}>
        {layerLabel(item.layerId)}
      </span>
      <span style={{ fontSize: 'var(--rh-size-footnote)', flex: 1 }} data-testid={`map-proximity-value-${testId}`}>
        {itemValue(item)}
      </span>
      {item.nearest.length > 0 && (
        <span
          style={{ fontSize: 'var(--rh-size-tag)', color: 'var(--rh-text-muted)', flexBasis: '100%' }}
          data-testid={`map-proximity-nearest-${testId}`}
        >
          {item.nearest
            .map((object) =>
              t('webAdmin.parcelMap.proximity.nearest', {
                locale: 'cs',
                values: { label: object.label, m: metres(object.distanceM) },
              }),
            )
            .join(' · ')}
        </span>
      )}
    </li>
  );
}

export interface ParcelProximityPanelProps {
  readonly caseId: string;
}

export function ParcelProximityPanel({ caseId }: ParcelProximityPanelProps): React.JSX.Element {
  const [open, setOpen] = React.useState(false);
  const [data, setData] = React.useState<ParcelProximityDto | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [failed, setFailed] = React.useState(false);
  /**
   * Zive dotazy na registry maji bezet JEDNOU za zakazku, ne pri kazdem
   * zavreni a otevreni sekce. Priznak je v refu, ne ve stavu: nesmi se na nem
   * prekreslovat a zaroven se na nej musi dat spolehnout hned.
   */
  const requestedRef = React.useRef<string | null>(null);

  /**
   * Nacita OBSLUHA KLIKU, ne efekt. Efekt zavisly na vlastnim `loading` se
   * pri prvnim behu sam zrusil uklidem (dotaz dojel do prazdna a sekce
   * zustala viset na „Měřím…") — zavod overeny testem v tomto kroku.
   */
  const load = (): void => {
    if (requestedRef.current === caseId) return;
    requestedRef.current = caseId;
    setLoading(true);
    setFailed(false);
    fetchParcelProximity(caseId)
      .then((result) => {
        setData(result);
      })
      .catch(() => {
        setFailed(true);
        /* Porucha nesmi sekci zamknout: dalsi rozbaleni to smi zkusit znovu. */
        requestedRef.current = null;
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <section className="rh-a2-map-proximity" data-testid="map-proximity-panel">
      <h3 className="rh-a2-map-proximity__heading">
        {t('webAdmin.parcelMap.proximity.heading', { locale: 'cs' })}
      </h3>
      <button
        type="button"
        className="rh-btn"
        aria-expanded={open}
        onClick={() => {
          const next = !open;
          setOpen(next);
          if (next) load();
        }}
        data-testid="map-proximity-toggle"
      >
        {t(
          open ? 'webAdmin.parcelMap.proximity.collapse' : 'webAdmin.parcelMap.proximity.expand',
          { locale: 'cs' },
        )}
      </button>
      {open && (
        <>
          {loading && (
            <p className="rh-a2-muted" data-testid="map-proximity-loading">
              {t('webAdmin.parcelMap.proximity.loading', { locale: 'cs' })}
            </p>
          )}
          {failed && (
            <p className="rh-a2-muted" role="alert" data-testid="map-proximity-error">
              {t('webAdmin.parcelMap.proximity.error', { locale: 'cs' })}
            </p>
          )}
          {data !== null && data.geometryMissing && (
            <p className="rh-a2-muted" data-testid="map-proximity-geometry-missing">
              {t('webAdmin.parcelMap.proximity.geometryMissing', { locale: 'cs' })}
            </p>
          )}
          {data !== null && !data.geometryMissing && (
            <>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }} data-testid="map-proximity-list">
                {data.items.map((item) => (
                  <ProximityRow key={item.layerId} item={item} />
                ))}
              </ul>
              <p className="rh-a2-muted" style={{ marginTop: 6 }} data-testid="map-proximity-note">
                {t('webAdmin.parcelMap.proximity.note', { locale: 'cs' })}
              </p>
            </>
          )}
        </>
      )}
    </section>
  );
}
